import { useCallback, useEffect, useRef, useState } from "react";
import { getEnv } from "helpers/environment";
import { io } from "socket.io-client";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import { setExecutionBatchDateTime, clearSelectedLogNodeId, addStepInfo, filterAndAddExecutionReport, endExecutionReport } from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { setContext, updateStepContextOutputData } from "redux/actions/flowDesigner/editorContextActionCreator";
import { stopFlowExecution, startFlowExecution } from "redux/actions/flowDesigner/tabsActionCreator";
import { COMMON_COMPONENT_NAMES } from "constants/flowDesigner";
import { autoUpdateCurrentFlow, setSavingFlowFlagReset } from "redux/actions/flowDesigner/flowsActionCreator";
import { tryCatchWrapper, isValidJsonString } from "helpers/common";
import { toast } from "react-toastify";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";
import { setExecutorSocketDown } from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { openFlows, thisUsersData } from "App";

export let executorSocket = null;

export const DesignerSockets = ({ getFlowInput }) => {
   const { currentSavedFlow, autoSavingFlowFlagSuccess } = useSelector((store) => store?.designer?.flows);
   const { executionBatchDateTime } = useSelector((store) => store?.designer?.flowExecution);
   const { user } = useAuth0();
   const currentSavedFlowRef = useRef(null);
   currentSavedFlowRef.current = currentSavedFlow;
   const dispatch = useDispatch();
   const executionStepsRef = useRef([]);
   const [saveFlowFromSocket, setSaveFlowFromSocket] = useState(false);
   const [pendingReportInfo, setPendingReportInfo] = useState(null);
   const { addLayoutTab } = useFlexLayout();
   const executionBatchRef = useRef(null);
   const sessionIdRef = useRef(null);
   const userIdRef = useRef(null);

   const openExecutionPanelAndAddReport = (payload) => {
      dispatch(setExecutionBatchDateTime(payload.batchDateTime));
      dispatch(filterAndAddExecutionReport(payload));
      dispatch(startFlowExecution());
   };
   
   const runExecutionStep = useCallback(() => {
      if (executionStepsRef.current.length) {
         const item = executionStepsRef.current.shift();
         
         if (item.type === 'step') {
            if (!item) return;

            const { stepInfo, context, sessionId } = item;

            const { flowId } = stepInfo;

            if (!flowId) return;
            let result = stepInfo?.response?.data ?? stepInfo?.response?.results;

            const isConnector =
               stepInfo.component && !COMMON_COMPONENT_NAMES[stepInfo.component];

            if (isConnector) {
               result = stepInfo?.response?.results || stepInfo?.response;
            }

            if (context) {
               dispatch(setContext(context));
            }

            if (result !== undefined) {
               dispatch(
                  updateStepContextOutputData(
                     stepInfo.componentKey,
                     result,
                     stepInfo.flowName
                  )
               );
            }

            dispatch(addStepInfo(stepInfo, sessionId));
         } else {
            dispatch(clearSelectedLogNodeId());
            
            const message = item.message, 
               targetUserId = message.targetUserId;
            
            if (targetUserId && targetUserId !== "*" && targetUserId !== userIdRef.current) return;

            const { flowId } = message;

            if (!flowId) return;

            dispatch(endExecutionReport(message));
            dispatch(stopFlowExecution());

            sessionIdRef.current = null;
            executionBatchRef.current = null;
            executionStepsRef.current = [];
         }
      }
   }, [dispatch]);

   useEffect(() => {
      if (!executionBatchDateTime) return;

      executionBatchRef.current = executionBatchDateTime;

      while (
         executionStepsRef.current.length &&
         executionStepsRef.current[0].stepInfo.execution_batch_datetime ===
            executionBatchRef.current
      ) {
         runExecutionStep();
      }
   }, [executionBatchDateTime, runExecutionStep]);

   useEffect(() => {
      if (!userIdRef.current && user) {
         userIdRef.current = user.cxfUserId;
      }
   }, [user]);

   useEffect(() => {
      const socketIoServerAddress = getEnv("EXECUTOR_API_SOCKET");

      executorSocket = io(socketIoServerAddress, {
         transports: ["websocket"],
         withCredentials: true
      }).on("connect", () => {
         executorSocket.emit("joinRoom", user?.stripeId);
         dispatch(setExecutorSocketDown(false));

         for (const [ flowId, tenantId ] of openFlows.entries())
            executorSocket.emit("joinFlowRoom", { tenantId, flowId, user: thisUsersData });
         
         console.info(`(Re)connected to cxf-executor socket.io server ${socketIoServerAddress} for flow "room" events`);
      }).on("disconnect", (reason) => {
         dispatch(setExecutorSocketDown(true));
         console.info(`Disonnected from cxf-executor socket.io server for flow "room" events, reason: ${reason}`);
      }).on("connect_error", (error) => {
         dispatch(setExecutorSocketDown(true));
         console.info(`Unable to connect to cxf-executor socket.io server for flow "room" events: ${error.message}`);
      }).on("newExecutionStep", ({ stepInfo, context, sessionId, targetUserId }) => {
         if (stepInfo.flowId !== currentSavedFlowRef.current?.id || (targetUserId && targetUserId !== "*" && targetUserId !== userIdRef.current))
            return;

         if (sessionIdRef.current === null)
            sessionIdRef.current = sessionId;
         else if (sessionIdRef.current !== sessionId)
            return;

         if (stepInfo?.isFirstStart) {
            dispatch(setExecutionBatchDateTime(stepInfo.execution_batch_datetime));

            return;
         }

         executionStepsRef.current.push({ type: 'step', stepInfo, context, sessionId });
         
         runExecutionStep();
      }).on("reportInfo", (message) => {
         const { flowId = "", batchDateTime = "", sessionId = "", targetUserId } = message;

         if (flowId !== currentSavedFlowRef.current?.id)
            return;

         if (targetUserId && targetUserId !== "*" && targetUserId !== userIdRef.current) {
            return;
         }

         if (sessionIdRef.current === null)
            sessionIdRef.current = sessionId;
         else if (sessionIdRef.current !== sessionId)
            return;

         if (batchDateTime) {
            setPendingReportInfo({
               flowId,
               batchDateTime,
               sessionId
            });
         }
      }).on("sendBatchDateTime", ({ batchDateTime, flow, sessionId, targetUserId }) => {
         if (flow.id !== currentSavedFlowRef.current?.id)
            return;

         if (targetUserId && targetUserId !== "*" && targetUserId !== userIdRef.current)
            return;

         if (sessionIdRef.current === null)
            sessionIdRef.current = sessionId;
         else if (sessionIdRef.current !== sessionId)
            return;

         const flow_settings = isValidJsonString(currentSavedFlowRef.current?.flow_settings) ? JSON.parse(currentSavedFlowRef.current?.flow_settings) : currentSavedFlowRef.current?.flow_settings ? currentSavedFlowRef.current?.flow_settings : {};
         const displayExecutionLogs = currentSavedFlowRef.current?.shared_status?.toLowerCase() === "active" ? (flow_settings?.display_execution_logs?.active ?? false) : (flow_settings?.display_execution_logs?.draft ?? true);

         if (batchDateTime && displayExecutionLogs) {
            setPendingReportInfo({
               flowId: flow.id,
               batchDateTime,
               sessionId
            });
         }
      }).on("endExecution", (message) => {
         if (message.flowId !== currentSavedFlowRef.current?.id)
            return;

         if (sessionIdRef.current !== message.sessionId)
            return;

         executionStepsRef.current.push({ type: 'end', message });

         runExecutionStep();
      }).on("messageResponse", (message) => {
         if (message.targetUserId && message.targetUserId !== "*" && message.targetUserId !== userIdRef.current)
            return;

         if (message?.messageType !== "success")
            dispatch(stopFlowExecution());
         
         if (toast[message?.messageType])
            toast[message?.messageType](message?.message);
      });
   }, []);

   useEffect(() => {
      if (autoSavingFlowFlagSuccess && currentSavedFlow) {
         dispatch(setSavingFlowFlagReset());
      }
   }, [autoSavingFlowFlagSuccess]);

   useEffect(() => {
      if (!saveFlowFromSocket) return;

      setSaveFlowFromSocket(false);
      const input = tryCatchWrapper(getFlowInput)(undefined, currentSavedFlow.name);
      dispatch(autoUpdateCurrentFlow(user?.stripeId, currentSavedFlow.id, input));
   }, [saveFlowFromSocket, currentSavedFlow]);

   useEffect(() => {
      if (pendingReportInfo) {
         addLayoutTab(FLEX_LAYOUT_COMPONENT.executionReport, `executionReport`, `Logs`);
         openExecutionPanelAndAddReport(pendingReportInfo);
         setPendingReportInfo(null);
      }
   }, [pendingReportInfo, addLayoutTab]);

   return <></>;
};
